import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Textarea } from './ui/textarea';
import { User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MentionTextarea({ 
  value, 
  onChange, 
  placeholder = "Add a comment...",
  className = "",
  onSubmit
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const textareaRef = useRef(null);

  // Debounce search
  const searchUsers = useCallback(async (query) => {
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
      setSuggestions(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (mentionSearch.length > 0 || mentionStartPos >= 0) {
      const timer = setTimeout(() => {
        searchUsers(mentionSearch);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [mentionSearch, searchUsers, mentionStartPos]);

  const handleChange = (e) => {
    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(text);

    // Check for @ mention trigger
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if it's a valid mention start (no space between @ and cursor, and @ is at start or after space)
      const charBeforeAt = lastAtIndex > 0 ? text[lastAtIndex - 1] : ' ';
      
      if (!textAfterAt.includes(' ') && (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0)) {
        setShowSuggestions(true);
        setMentionSearch(textAfterAt);
        setMentionStartPos(lastAtIndex);
        return;
      }
    }
    
    setShowSuggestions(false);
    setMentionSearch('');
    setMentionStartPos(-1);
  };

  const insertMention = (user) => {
    const beforeMention = value.substring(0, mentionStartPos);
    const afterMention = value.substring(mentionStartPos + mentionSearch.length + 1);
    const newValue = `${beforeMention}@${user.name}${afterMention.startsWith(' ') ? '' : ' '}${afterMention}`;
    
    onChange(newValue);
    setShowSuggestions(false);
    setMentionSearch('');
    setMentionStartPos(-1);
    
    // Focus back to textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          insertMention(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setMentionSearch('');
        setMentionStartPos(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
      />
      
      {/* Mention Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
          {suggestions.map((user, index) => (
            <button
              key={user.id}
              type="button"
              onClick={() => insertMention(user)}
              onMouseDown={(e) => e.preventDefault()} // Prevent blur on click
              className={`w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors ${
                index === selectedIndex
                  ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white'
                  : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
              }`}
            >
              <div className="w-6 h-6 rounded-full bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-zinc-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user.email}</p>
              </div>
              <span className="text-xs text-zinc-500 capitalize">{user.role}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function to render comment text with highlighted mentions
export function renderMentionText(text) {
  if (!text) return null;
  
  // Match @Username patterns (names can have spaces)
  const mentionRegex = /@([A-Za-z][A-Za-z\s]*?)(?=\s@|$|\s[^A-Za-z]|\s*$)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {text.substring(lastIndex, match.index)}
        </span>
      );
    }
    
    // Add highlighted mention
    parts.push(
      <span 
        key={`mention-${match.index}`} 
        className="text-[#C3002F] font-medium bg-[#C3002F]/10 px-1 rounded"
      >
        @{match[1]}
      </span>
    );
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(
      <span key={`text-${lastIndex}`}>
        {text.substring(lastIndex)}
      </span>
    );
  }
  
  return parts.length > 0 ? parts : text;
}
